import { MarketPlace } from '@amzn/fire-tv-campaign-automation-service';

import { ProvisionedConstants, STAGE } from './constants';

export enum FeatureFlag {
  RdpIntegration = 'rdpIntegration',
  EmbeddedMode = 'embeddedMode',
  OverlaySection = 'overlaySection',
}

const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.RdpIntegration]: false,
  [FeatureFlag.EmbeddedMode]: false,
  [FeatureFlag.OverlaySection]: false,
};

const STAGE_OVERRIDES: Partial<Record<STAGE, Partial<Record<FeatureFlag, boolean>>>> = {
  [STAGE.DEV]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.BETA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.GAMMA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.PROD_NA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: false,
  },
};

/** Restricts a flag to specific marketplaces after the stage gate passes. */
export interface MarketplaceOverrideConfig {
  allowedMarketplaces?: MarketPlace[];
}

/** Omitting a flag means it is available in all marketplaces (stage permitting). */
const MARKETPLACE_OVERRIDES: Partial<Record<FeatureFlag, MarketplaceOverrideConfig>> = {
  [FeatureFlag.RdpIntegration]: { allowedMarketplaces: [MarketPlace.US] },
};

/**
 * Returns whether a feature flag is enabled for the current stage and marketplace.
 * Prefer `useFeatureFlag` inside React components — it reads marketplace from context.
 *
 * @param flag        - The feature flag to evaluate.
 * @param marketplace - Optional. When omitted, only the stage gate applies.
 */
export const isFeatureEnabled = (flag: FeatureFlag, marketplace?: MarketPlace): boolean => {
  const stageResult =
    { ...DEFAULT_FLAGS, ...STAGE_OVERRIDES[ProvisionedConstants.STAGE as STAGE] }[flag] ?? false;

  if (!stageResult || marketplace === undefined) return stageResult;

  const override = MARKETPLACE_OVERRIDES[flag];
  return override?.allowedMarketplaces
    ? override.allowedMarketplaces.includes(marketplace)
    : stageResult;
};
